import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  RefObject,
} from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  clampPx,
  FAB_SIZE_PX,
  FabPx,
  FabRatio,
  FabSideX,
  FabSideY,
  fromRatio,
  isDrag,
  keyboardStep,
  readStoredRatio,
  sides,
  storeRatio,
  toRatio,
} from './fabPosition'

/** Baloncuk ile logo arasındaki boşluk (`.introNudge` / `.tipLeft::after` ile aynı) + kenar payı. */
const BUBBLE_GAP_PX = 12 + 8

interface DragSession {
  pointerId: number
  startX: number
  startY: number
  originLeft: number
  originTop: number
  moved: boolean
}

const viewport = () => ({ vw: window.innerWidth, vh: window.innerHeight })

function initialPosition(): { ratio: FabRatio | null; px: FabPx | null } {
  const ratio = readStoredRatio()
  if (!ratio) return { ratio: null, px: null }
  const { vw, vh } = viewport()
  return { ratio, px: fromRatio(ratio, FAB_SIZE_PX, vw, vh) }
}

/**
 * Kapalı haldeki başlatıcının (FAB + baloncuklar) sürüklenmesi (spec §5). Kayıt yoksa `.root`'un
 * bugünkü CSS konumu geçerlidir; ilk sürüklemede kökün o anki yeri başlangıç alınır.
 * `enabled=false` (panel açık) iken kök stilsiz kalır → panel bugünkü yerinde açılır.
 */
export function useDraggableFab(rootRef: RefObject<HTMLElement>, enabled: boolean) {
  const [initial] = useState(initialPosition)
  const [position, setPosition] = useState<FabPx | null>(initial.px)
  const [dragging, setDragging] = useState(false)
  const ratioRef = useRef<FabRatio | null>(initial.ratio)
  const sessionRef = useRef<DragSession | null>(null)
  /** Sürüklemenin ardından gelen `click` yutulur (panel açılmaz). */
  const swallowClickRef = useRef(false)
  const swallowTimerRef = useRef<number | null>(null)

  const commit = useCallback((px: FabPx) => {
    const { vw, vh } = viewport()
    const ratio = toRatio(px.left, px.top, FAB_SIZE_PX, vw, vh)
    ratioRef.current = ratio
    storeRatio(ratio)
  }, [])

  // Pencere boyutu değişince kayıtlı oran yeniden piksele çevrilir ve sınırlanır.
  useEffect(() => {
    const onResize = () => {
      const ratio = ratioRef.current
      if (!ratio) return
      const { vw, vh } = viewport()
      setPosition(fromRatio(ratio, FAB_SIZE_PX, vw, vh))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(
    () => () => {
      if (swallowTimerRef.current !== null) window.clearTimeout(swallowTimerRef.current)
    },
    []
  )

  /** Kayıt yoksa kökün ekrandaki yeri (bugünkü CSS konumu). */
  const currentPosition = (): FabPx | null => {
    if (position) return position
    const rect = rootRef.current?.getBoundingClientRect()
    return rect ? { left: rect.left, top: rect.top } : null
  }

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return
    const origin = currentPosition()
    if (!origin) return
    swallowClickRef.current = false
    sessionRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originLeft: origin.left,
      originTop: origin.top,
      moved: false,
    }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      /* eski tarayıcı: yakalama olmadan da hareket izlenir */
    }
  }

  const onPointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    const session = sessionRef.current
    if (!session || session.pointerId !== e.pointerId) return
    const dx = e.clientX - session.startX
    const dy = e.clientY - session.startY
    if (!session.moved) {
      if (!isDrag(dx, dy)) return
      session.moved = true
      setDragging(true)
    }
    const { vw, vh } = viewport()
    setPosition(clampPx(session.originLeft + dx, session.originTop + dy, FAB_SIZE_PX, vw, vh))
  }

  const endDrag = (e: ReactPointerEvent<HTMLElement>) => {
    const session = sessionRef.current
    if (!session || session.pointerId !== e.pointerId) return
    sessionRef.current = null
    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      /* yakalama zaten bırakılmış */
    }
    if (!session.moved) return

    setDragging(false)
    const { vw, vh } = viewport()
    const final = clampPx(
      session.originLeft + (e.clientX - session.startX),
      session.originTop + (e.clientY - session.startY),
      FAB_SIZE_PX,
      vw,
      vh
    )
    setPosition(final)
    commit(final)

    // Bırakışı izleyen `click` aynı görevde gelir; gelmezse bayrak bir sonraki tıkı yutmasın.
    swallowClickRef.current = true
    if (swallowTimerRef.current !== null) window.clearTimeout(swallowTimerRef.current)
    swallowTimerRef.current = window.setTimeout(() => {
      swallowClickRef.current = false
      swallowTimerRef.current = null
    }, 0)
  }

  /** `true` → bu tık bir sürüklemenin sonudur, panel açılmamalı. */
  const consumeDragClick = (): boolean => {
    if (!swallowClickRef.current) return false
    swallowClickRef.current = false
    return true
  }

  /** `Alt + ok` 16 px kaydırır; diğer tuşlar (Enter/Space) bugünkü gibi düğmeye kalır. */
  const onKeyDown = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (!e.altKey) return
    const step = keyboardStep(e.key)
    if (!step) return
    e.preventDefault()
    const origin = currentPosition()
    if (!origin) return
    const { vw, vh } = viewport()
    const next = clampPx(origin.left + step.dx, origin.top + step.dy, FAB_SIZE_PX, vw, vh)
    setPosition(next)
    commit(next)
  }

  const active = enabled && position !== null
  let sideX: FabSideX = 'right'
  let sideY: FabSideY = 'bottom'
  let rootStyle: CSSProperties | undefined
  if (active && position) {
    const { vw, vh } = viewport()
    const side = sides(position.left, position.top, FAB_SIZE_PX, vw, vh)
    sideX = side.x
    sideY = side.y
    // Baloncuk logonun açıldığı yandaki boşluğa sığsın: hiçbir konumda ekrandan taşmaz.
    const room = side.x === 'left' ? vw - position.left - FAB_SIZE_PX - BUBBLE_GAP_PX : position.left - BUBBLE_GAP_PX
    rootStyle = {
      left: position.left,
      top: position.top,
      right: 'auto',
      bottom: 'auto',
      '--gini-bubble-room': `${Math.max(Math.round(room), 0)}px`,
    } as CSSProperties
  }

  return {
    rootStyle,
    sideX,
    sideY,
    dragging,
    consumeDragClick,
    fabHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onKeyDown,
    },
  }
}
